/**
 * FormModal - modal chrome (title + scrollable body + Cancel/Submit footer)
 * wrapped around a TanStack form. The caller owns the form instance (defaults,
 * validators, onSubmit) and passes the fields as children; this only renders the
 * chrome and wires the footer's SubmitButton to the form context.
 *
 * Responsive: a `Modal` renders a centered dialog on desktop and a bottom drawer
 * on mobile (< 1024px).
 *
 * @example
 * ```tsx
 * <FormModal
 *   open={open}
 *   onOpenChange={(next) => { if (!next) close(); }}
 *   form={form}
 *   title="Create tag"
 *   submitLabel="Create"
 *   isPending={mutation.isPending}
 * >
 *   <TextField name="name" label="Tag name" autoFocus />
 * </FormModal>
 * ```
 */

import type { ReactNode } from "react";
import { Button } from "../button";
import type { DialogSize } from "../dialog";
import { Form } from "../form/form";
import { SubmitButton } from "../form/submit-button";
import {
  Modal,
  ModalClose,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalTitle,
} from "./modal";

export interface FormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The `useAppForm` instance — provides submit handling + the AppForm context. */
  // The form instance is deeply generic; callers pin it at the call site.
  form: any;
  title: ReactNode;
  description?: ReactNode;
  /** The form fields. */
  children: ReactNode;
  submitLabel?: ReactNode;
  cancelLabel?: ReactNode;
  /** Mutation-in-flight flag — disables the submit button and shows a spinner. */
  isPending?: boolean;
  size?: DialogSize;
}

export function FormModal({
  open,
  onOpenChange,
  form,
  title,
  description,
  children,
  submitLabel = "Save",
  cancelLabel = "Cancel",
  isPending,
  size = "sm",
}: FormModalProps) {
  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent className="flex flex-col" dialogProps={{ size }}>
        <Form form={form} className="flex flex-col flex-1 min-h-0">
          <ModalTitle>{title}</ModalTitle>
          {description ? (
            <ModalDescription className="px-3 -mt-2 pb-3 text-left xl:px-6">
              {description}
            </ModalDescription>
          ) : null}
          <div className="flex flex-col flex-1 gap-6 px-3 pt-1 pb-6 min-h-0 overflow-y-auto xl:px-6">
            {children}
          </div>
          <ModalFooter>
            <ModalClose asChild>
              <Button type="button" kind="outline" variant="neutral">
                {cancelLabel}
              </Button>
            </ModalClose>
            <SubmitButton isPending={isPending}>{submitLabel}</SubmitButton>
          </ModalFooter>
        </Form>
      </ModalContent>
    </Modal>
  );
}
